import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getReceipts, billingMe } from '../services/api';
import { Buildings, Users, Receipt, TrendUp, TrendDown, Warning, CheckCircle, Robot, CaretRight } from '@phosphor-icons/react';

const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function Dashboard() {
  const navigate = useNavigate();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth); // 0 = full year
  const [stats, setStats] = useState(null);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  const years = useMemo(() => {
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  }, [currentYear]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const params = { year };
        if (month && month > 0) params.month = month;
        const [statsRes, receiptsRes, billingRes] = await Promise.all([
          getDashboardStats(params),
          getReceipts(),
          billingMe().catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setRecentReceipts(receiptsRes.data.slice(0, 5));
        setBilling(billingRes.data);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [year, month]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Build query string used by linked pages so they can pre-filter (Transactions reads ?year & ?month)
  const txParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('year', String(year));
    if (month && month > 0) p.set('month', String(month));
    return p.toString();
  }, [year, month]);

  const goToTransactions = (type) => {
    const p = new URLSearchParams(txParams);
    if (type) p.set('type', type);
    navigate(`/transactions?${p.toString()}`);
  };

  const periodLabel = month && month > 0 ? `${MONTHS_DE[month - 1]} ${year}` : `Jahr ${year}`;

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
      {/* Header + period filter */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Übersicht für <span className="font-medium text-slate-700">{periodLabel}</span></p>
        </div>
        <div className="flex items-center gap-2" data-testid="dashboard-period-filter">
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="border border-slate-200 rounded px-3 py-2 text-sm bg-white"
            data-testid="dashboard-month-select"
          >
            <option value={0}>Gesamtes Jahr</option>
            {MONTHS_DE.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border border-slate-200 rounded px-3 py-2 text-sm bg-white"
            data-testid="dashboard-year-select"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <StatCard
          icon={<Buildings weight="bold" className="w-5 h-5 lg:w-6 lg:h-6" />}
          label="Immobilien"
          value={stats?.properties_count || 0}
          onClick={() => navigate('/properties')}
          testId="stat-properties"
        />
        <StatCard
          icon={<Buildings weight="regular" className="w-5 h-5 lg:w-6 lg:h-6" />}
          label="Einheiten"
          value={stats?.units_count || 0}
          onClick={() => navigate('/properties')}
          testId="stat-units"
        />
        <StatCard
          icon={<Users weight="bold" className="w-5 h-5 lg:w-6 lg:h-6" />}
          label="Mieter"
          value={stats?.tenants_count || 0}
          onClick={() => navigate('/tenants')}
          testId="stat-tenants"
        />
        <StatCard
          icon={<Receipt weight="bold" className="w-5 h-5 lg:w-6 lg:h-6" />}
          label="Zur Prüfung"
          value={stats?.receipts_pending || 0}
          highlight={stats?.receipts_pending > 0}
          onClick={() => navigate('/receipts')}
          testId="stat-receipts-pending"
        />
      </div>

      {/* Financial Overview - clickable */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <button
          type="button"
          onClick={() => goToTransactions('income')}
          className="text-left bg-white border border-gray-200 p-4 sm:p-6 hover:border-emerald-400 hover:shadow-md transition-all group"
          data-testid="income-card"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 flex items-center justify-center">
                <TrendUp weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
              <span className="text-xs tracking-widest uppercase font-bold text-slate-500">Einnahmen</span>
            </div>
            <CaretRight weight="bold" className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 transition-colors" />
          </div>
          <p className="font-mono text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-emerald-600">
            {formatCurrency(stats?.monthly_income)}
          </p>
        </button>

        <button
          type="button"
          onClick={() => goToTransactions('expense')}
          className="text-left bg-white border border-gray-200 p-4 sm:p-6 hover:border-red-400 hover:shadow-md transition-all group"
          data-testid="expense-card"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 flex items-center justify-center">
                <TrendDown weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
              <span className="text-xs tracking-widest uppercase font-bold text-slate-500">Ausgaben</span>
            </div>
            <CaretRight weight="bold" className="w-4 h-4 text-slate-300 group-hover:text-red-600 transition-colors" />
          </div>
          <p className="font-mono text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-red-600">
            {formatCurrency(stats?.monthly_expenses)}
          </p>
        </button>

        <button
          type="button"
          onClick={() => goToTransactions()}
          className="text-left bg-white border border-gray-200 p-4 sm:p-6 hover:border-slate-400 hover:shadow-md transition-all group"
          data-testid="net-income-card"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center ${stats?.net_income >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {stats?.net_income >= 0 ? (
                  <TrendUp weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                ) : (
                  <TrendDown weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                )}
              </div>
              <span className="text-xs tracking-widest uppercase font-bold text-slate-500">Netto</span>
            </div>
            <CaretRight weight="bold" className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
          </div>
          <p className={`font-mono text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter ${stats?.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(stats?.net_income)}
          </p>
        </button>
      </div>

      {/* Recent Receipts */}
      <div className="bg-white border border-gray-200" data-testid="recent-receipts-section">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900">Letzte Belege</h2>
          <button
            onClick={() => navigate('/receipts')}
            className="text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="view-all-receipts"
          >
            Alle anzeigen <CaretRight weight="bold" className="w-3 h-3" />
          </button>
        </div>
        {recentReceipts.length === 0 ? (
          <div className="p-6 sm:p-8 text-center text-slate-500 text-sm sm:text-base">
            Keine Belege vorhanden. Laden Sie Ihren ersten Beleg hoch.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentReceipts.map((receipt) => (
              <button
                key={receipt.id}
                type="button"
                onClick={() => navigate(`/receipts?id=${receipt.id}`)}
                className="w-full text-left px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid={`receipt-item-${receipt.id}`}
              >
                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center ${receipt.status === 'auto_mapped' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                    {receipt.status === 'auto_mapped' ? (
                      <CheckCircle weight="fill" className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                    ) : (
                      <Warning weight="fill" className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 text-sm sm:text-base truncate">{receipt.vendor || receipt.filename}</p>
                    <p className="text-xs sm:text-sm text-slate-500 truncate">{receipt.category} • {receipt.date || 'Kein Datum'}</p>
                  </div>
                </div>
                <div className="text-right ml-3 flex-shrink-0">
                  <p className="font-mono font-medium text-slate-900 text-sm sm:text-base">
                    {receipt.amount ? formatCurrency(receipt.amount) : '—'}
                  </p>
                  <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 text-xs font-medium uppercase tracking-wider ${
                    receipt.status === 'auto_mapped' 
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                      : 'bg-amber-100 text-amber-800 border border-amber-200'
                  }`}>
                    {receipt.ai_confidence ? `${receipt.ai_confidence}%` : '—'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* AI Cost Widget */}
      {billing && (
        <div className="bg-white border border-violet-200 p-5 sm:p-6 mt-4" data-testid="ai-cost-widget">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 flex items-center justify-center">
                <Robot weight="bold" className="w-5 h-5 text-violet-700" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-violet-700">KI-Kosten {MONTHS_DE[billing.month - 1]} {billing.year}</p>
                <p className="font-mono text-2xl sm:text-3xl font-black tracking-tighter text-slate-900 mt-0.5">
                  {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 4 }).format(billing.kosten_euro)}
                </p>
              </div>
            </div>
            <div className="flex gap-6 sm:gap-8 text-sm">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Anfragen</p>
                <p className="font-mono text-xl font-bold text-slate-900">{billing.anfragen}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Tokens</p>
                <p className="font-mono text-xl font-bold text-slate-900">{new Intl.NumberFormat('de-DE').format(billing.tokens_total)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, highlight, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white border p-4 sm:p-6 hover:shadow-md transition-all group ${highlight ? 'border-amber-300 bg-amber-50 hover:border-amber-500' : 'border-gray-200 hover:border-slate-400'}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center ${highlight ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
            {icon}
          </div>
          <span className="text-xs tracking-widest uppercase font-bold text-slate-500 hidden sm:block">{label}</span>
        </div>
        <CaretRight weight="bold" className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
      </div>
      <p className="font-mono text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-slate-900">{value}</p>
      <span className="text-xs tracking-widest uppercase font-bold text-slate-500 sm:hidden mt-1 block">{label}</span>
    </button>
  );
}
